<template>
  <div class="mt-6 px-6">
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="48" color="primary" />
    </v-overlay>

    <v-data-table
      :headers="headersTable"
      :items="tableItems"
      item-key="id"
      :items-per-page="5"
      :footer-props="{
        'items-per-page-options': [5],
        'items-per-page-text': 'Itens por página:'
      }"
      class="elevation-0"
      :loading="loading"
      loading-text="Carregando dados..."
      no-data-text="Nenhum dado encontrado"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td v-for="col in colunas" :key="col.key" :class="getColumnClass(col)">
            {{ col.value ? col.value(item[col.key]) : item[col.key] }}
          </td>
        </tr>
      </template>

      <template v-slot:[`body.append`]>
        <tr class="font-weight-bold grey lighten-3 total-row">
          <td class="pr-4 text-right">
            <strong>Total</strong>
          </td>
          <td v-for="(col, index) in colunas.slice(1)" :key="'total-' + index" :class="getColumnClass(col)">
            <strong v-if="colunasComTotal.includes(col.key)">
              {{ formatNumber(calcularTotal(col.key)) }}
            </strong>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import DashboardQuantitativosService from '../../../../services/DashboardQuantitativosService';

  export default {
    name: 'TabelaEntidades',

    props: {
      entidade: {
        type: Object,
        required: true
      },
      filtrosAtivos: {
        type: Object,
        default: () => ({})
      },
      tipo: {
        type: String,
        default: ''
      }
    },

    data() {
      return {
        loading: false,
        tableItems: [],
        configColunas: {
          alimentador: [
            { key: 'municipio', label: 'Município', align: 'start' },
            { key: 'alimentador', label: 'Alimentador', align: 'start' },
            { key: 'conjunto', label: 'Conjunto', align: 'start' },
            { key: 'regional', label: 'Regional', align: 'start' },
            {
              key: 'qtd',
              label: 'Quantidade',
              align: 'end',
              value: this.formatNumber
            }
          ],
          trafosPorPotencia: [
            { key: 'municipio', label: 'Município', align: 'start' },
            { key: 'are_loc', label: 'Localização', align: 'center' },
            { key: 'pot', label: 'Potência', align: 'center' },
            { key: 'fas_con', label: 'Fase', align: 'center' },
            {
              key: 'qtd',
              label: 'Quantidade',
              align: 'end',
              value: this.formatNumber
            }
          ],
          localizacao: [
            { key: 'municipio', label: 'Município', align: 'start' },
            {
              key: 'rural',
              label: 'Rural',
              align: 'end',
              value: this.formatNumber
            },
            {
              key: 'urbana',
              label: 'Urbana',
              align: 'end',
              value: this.formatNumber
            },
            {
              key: 'qtd',
              label: 'Total',
              align: 'end',
              value: this.formatNumber
            }
          ]
        }
      };
    },

    computed: {
      colunas() {
        return this.configColunas[this.tipo];
      },

      headersTable() {
        return this.colunas.map((col) => ({
          text: col.label,
          value: col.key,
          align: col.align || 'start',
          sortable: true
        }));
      },

      colunasComTotal() {
        const totaisPorTipo = {
          alimentador: ['qtd'],
          trafosPorPotencia: ['qtd'],
          localizacao: ['rural', 'urbana', 'qtd']
        };

        return totaisPorTipo[this.tipo] || ['qtd'];
      }
    },

    mounted() {
      this.getDados();
    },

    methods: {
      getDados() {
        this.loading = true;

        const serviceMethods = {
          alimentador: () => DashboardQuantitativosService.getPorAlimentador(this.entidade.key, this.filtrosAtivos),
          trafosPorPotencia: () => DashboardQuantitativosService.getTrafosPorPotencia(this.entidade.key, this.filtrosAtivos),
          localizacao: () => DashboardQuantitativosService.getPorLocalizacao(this.entidade.key, this.filtrosAtivos)
        };

        const serviceMethod = serviceMethods[this.tipo];

        serviceMethod()
          .then(({ data }) => {
            this.tableItems = this.formatarDados(data);
          })
          .catch((err) => {
            console.error('Erro ao buscar dados da tabela:', err);
            this.$toast.error('Erro ao buscar detalhamento da tabela.', '', {
              position: 'topRight'
            });
          })
          .finally(() => {
            this.loading = false;
          });
      },

      formatarDados(data) {
        const formatters = {
          alimentador: (item, index) => ({
            id: index,
            municipio: item.municipio || '-',
            alimentador: item.alimentador || '(Não informado)',
            conjunto: item.conjunto || '-',
            regional: item.regional || '-',
            qtd: Number(item.qtd) || 0
          }),
          trafosPorPotencia: (item, index) => ({
            id: index,
            municipio: item.municipio || '-',
            are_loc: item.are_loc || '-',
            pot: item.pot || '-',
            fas_con: item.fas_con || '-',
            qtd: Number(item.qtd) || 0
          }),
          localizacao: (item, index) => ({
            id: index,
            municipio: item.municipio || '-',
            rural: Number(item.rural) || 0,
            urbana: Number(item.urbana) || 0,
            qtd: Number(item.total) || Number(item.qtd) || 0
          })
        };

        const formatter = formatters[this.tipo];
        return data.map((item, index) => formatter(item, index));
      },

      calcularTotal(coluna) {
        return this.tableItems.reduce((acc, item) => {
          return acc + (Number(item[coluna]) || 0);
        }, 0);
      },

      getColumnClass(col) {
        const classes = [];

        if (col.align === 'end') {
          classes.push('text-right', 'pr-4');
        } else if (col.align === 'center') {
          classes.push('text-center');
        }

        return classes.join(' ');
      },

      formatNumber(value) {
        if (value === null || value === undefined || value === '-') return value || '0';

        return Number(value).toLocaleString('pt-BR', {
          maximumFractionDigits: 2
        });
      }
    },

    watch: {
      entidade: {
        handler() {
          this.getDados();
        },
        deep: true
      },
      filtrosAtivos: {
        handler() {
          this.getDados();
        },
        deep: true
      },
      tipo: {
        handler() {
          this.getDados();
        }
      }
    }
  };
</script>

<style scoped>
  .total-row {
    border-top: 2px solid #ddd !important;
  }

  .total-row td {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
    font-size: 14px !important;
  }

  ::v-deep .v-data-table-header th {
    font-weight: 600 !important;
    font-size: 13px !important;
  }

  ::v-deep .v-data-table tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.02) !important;
  }
</style>